import { useState, useEffect, useRef } from "react"
import LoadingPopup from '../misc/Loading.js';
import { ChevronDown, ChevronUp, X, FileText, History, Zap, CheckCircle } from 'lucide-react';
import API from '../../services/api.js';
import { usePolling } from "../../services/PollingContext.js";
import { useAuth } from '../../context/AuthContext';
const EnhancedSelectField = ({
  name,
  value,
  handleChange,
  handleFocus,
  handleBlur,
  focusedField,
  options,
  placeholder,
  applicationDetails,
  isLoading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Update the filteredOptions logic to show loading state
  const getOptionsToShow = () => {
    if (isLoading) {
      return [];
    }
    return options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredOptions = getOptionsToShow();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectOption = (optionValue, optionLabel) => {
    handleChange({ target: { name, value: optionValue } });
    setIsOpen(false);
    setSearchTerm('');
  };

  const selectedOption = options.find(option => option.value === value);

  return (
    <div className="w-full transform transition-all duration-500 hover:scale-102 relative z-[1] dropdown-container" ref={dropdownRef}>
      <div className="relative group">
        {/* Main Select Button */}
        <button
          type="button"
          onClick={() => {
            setIsOpen(!isOpen);
            handleFocus(name);
          }}
          onBlur={handleBlur}
          className={`w-full bg-[#232325]/90 backdrop-blur-sm border-2 ${
            focusedField === name || isOpen
              ? 'border-blue-500 shadow-lg shadow-blue-500/30 scale-105'
              : 'border-[#232325] hover:border-blue-400/50 hover:shadow-md hover:shadow-blue-500/10'
          } px-6 py-4 rounded-2xl text-left text-sm focus:outline-none transition-all duration-300 transform hover:translate-y-[-2px] cursor-pointer flex items-center justify-between`}
        >
          <span className={selectedOption ? 'text-white' : 'text-gray-500'}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown Menu - Updated z-index and positioning */}
        {isOpen && (
          <div
            className="absolute top-full left-0 right-0 mt-2 bg-[#232325]/95 backdrop-blur-xl border border-[#232325] rounded-2xl shadow-2xl shadow-black/50 max-h-80 overflow-hidden animate-in slide-in-from-top-2 duration-200 dropdown-menu"
            style={{ position: 'absolute', zIndex: 10000 }}
          >
            {/* Search Input */}
            <div className="p-3 border-b border-gray-700/50">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-10 pr-4 py-2 bg-[#1a1a1c] border border-gray-600/30 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-colors"
                  autoFocus
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Options List - Updated with loading states */}
            <div className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-track-gray-800 scrollbar-thumb-gray-600">
              {isLoading ? (
                // Loading state for dropdown
                <div className="px-4 py-8 text-center text-gray-400">
                  <div className="flex flex-col items-center space-y-3">
                    <div className="relative">
                      <svg className="animate-spin w-8 h-8 text-blue-400" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <p className="text-sm">Loading...</p>
                    <p className="text-xs opacity-75">Please wait a moment</p>
                  </div>
                </div>
              ) : filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => {
                  const isSelected = option.value === value;
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSelectOption(option.value, option.label)}
                      className={`w-full px-4 py-3 text-left hover:bg-[#1a1a1c] transition-colors duration-150 border-b border-gray-700/30 last:border-b-0 ${
                        isSelected ? 'bg-blue-600/20 text-blue-300' : 'text-white'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3">
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              isSelected ? 'bg-blue-400' : 'bg-gray-500'
                            }`} />
                            <div className="min-w-0 flex-1">
                              <p className="font-medium truncate text-sm">
                                {option.label}
                              </p>
                            </div>
                          </div>
                        </div>
                        {isSelected && (
                          <svg className="w-4 h-4 text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-8 text-center text-gray-400">
                  <svg className="mx-auto h-8 w-8 mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-sm">No options found</p>
                  <p className="text-xs">Try adjusting your search</p>
                </div>
              )}
            </div>

            {/* Footer with count - Updated to hide during loading */}
            {!isLoading && filteredOptions.length > 0 && (
              <div className="px-4 py-2 bg-[#1a1a1c] border-t border-gray-700/50">
                <p className="text-xs text-gray-400 text-center">
                  {filteredOptions.length} of {options.length} option(s)
                </p>
              </div>
            )}
          </div>
        )}

        {/* Focus/Hover Effects */}
        <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/20 via-blue-400/20 to-blue-300/20 opacity-0 ${
          focusedField === name || isOpen ? 'opacity-100' : 'group-hover:opacity-50'
        } transition-opacity duration-300 pointer-events-none blur-xl`} />
      </div>
    </div>
  );
};

export default function Sysprompt() {
  const { role } = useAuth();
  const isViewer = role === 'viewer';
  const [prompt, setPrompt] = useState(() => localStorage.getItem("prompt") || "");
  
  useEffect(() => {
    localStorage.setItem("prompt", prompt);
  }, [prompt]);

  const [loading] = useState(() => {
    const storedLoading = sessionStorage.getItem("loading");
    return storedLoading ? JSON.parse(storedLoading) : false;
  });
  const [enhancing, setEnhancing] = useState(() => {
    const storedEnhancing = sessionStorage.getItem("enhancing");
    return storedEnhancing ? JSON.parse(storedEnhancing) : false;
  });
  const [results, setResults] = useState(() => {
    const storedResults = sessionStorage.getItem("results");
    return storedResults ? JSON.parse(storedResults) : null;
  });
  const [improvedPrompt, setImprovedPrompt] = useState("")
  const [promptHistory, setPromptHistory] = useState([]);
  const [activeTab, setActiveTab] = useState(() => (role === 'viewer' ? "history" : "prompt"));
  const [deletingId, setDeletingId] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedPrompts, setExpandedPrompts] = useState({});
  const [isUsingHistoryPrompt, setIsUsingHistoryPrompt] = useState(false);
  const [enhancingTaskId, setEnhancingTaskId] = useState(() => sessionStorage.getItem("enhancingTaskId") || null);
  const [, setAnalyzingTaskId] = useState(() => sessionStorage.getItem("analyzingTaskId") || null);
  const [attackProvider, setAttackProvider] = useState("OpenAI");
  const [attackModel, setAttackModel] = useState("gpt-3.5-turbo");
  const [models, setModels] = useState([
    { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" },
    { value: "gpt-4", label: "GPT-4" },
    { value: "gpt-4-turbo", label: "GPT-4 Turbo" },
  ]);
  const [providerOptions, setProviderOptions] = useState([
    { value: "OpenAI", label: "OpenAI" },
  ]);
  const [providerModelsMap, setProviderModelsMap] = useState({});
  const [providersLoading, setProvidersLoading] = useState(false);
  const [focusedField, setFocusedField] = useState('');

  const handleProviderChange = (e) => {
    const provider = e.target.value;
    setAttackProvider(provider);
    const nextModels = providerModelsMap[provider] || [];
    setModels(nextModels);
    if (!nextModels.find((m) => m.value === attackModel)) {
      setAttackModel(nextModels[0]?.value || "");
    }
  };

  useEffect(() => {
    if (isViewer && activeTab !== "history") {
      setActiveTab("history");
    }
  }, [isViewer, activeTab]);

  useEffect(() => {
    const loadProviders = async () => {
      setProvidersLoading(true);
      try {
        const response = await API.get("/ai-providers/");
        const providers = response?.data?.providers || [];
        const hardenerProviders = providers.filter((p) => p.module_available !== false && !p.redteam_only);
        if (hardenerProviders.length === 0) return;

        const options = hardenerProviders.map((p) => ({
          value: p.label,
          label: p.label,
        }));
        const modelsMap = {};
        hardenerProviders.forEach((p) => {
          modelsMap[p.label] = (p.hardener_models || []).map((m) => ({
            value: m.id,
            label: m.label,
          }));
        });

        setProviderOptions(options);
        setProviderModelsMap(modelsMap);


      } catch (error) {
        console.error("Failed to load providers:", error);
      } finally {
        setProvidersLoading(false);
      }
    };

    loadProviders();
  }, []);

  useEffect(() => {
    if (Object.keys(providerModelsMap).length === 0) return;
    if (!providerModelsMap[attackProvider]) {
      const firstProvider = Object.keys(providerModelsMap)[0];
      const firstModels = providerModelsMap[firstProvider] || [];
      setAttackProvider(firstProvider);
      setModels(firstModels);
      setAttackModel(firstModels[0]?.value || "");
      return;
    }
    const currentModels = providerModelsMap[attackProvider];
    setModels(currentModels);
    if (!currentModels.some((model) => model.value === attackModel)) {
      setAttackModel(currentModels[0]?.value || "");
    }
  }, [providerModelsMap, attackProvider, attackModel]);

  // Collapsible sections state

  const [resultsSectionOpen, setResultsSectionOpen] = useState(true);
  const [enhancedSectionOpen, setEnhancedSectionOpen] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(() => {
    const stored = sessionStorage.getItem("isAnalyzing");
    return stored ? JSON.parse(stored) : false;
  });

  const { startPolling, stopPolling, pollingResult, showNotification } = usePolling();

  useEffect(() => {
    sessionStorage.setItem("isAnalyzing", JSON.stringify(isAnalyzing));
  }, [isAnalyzing]);

  useEffect(() => {
    if (pollingResult) {
      const { status, result, taskType } = pollingResult;

      if (taskType === 'analyze') {
        if (status === 'SUCCESS') {
          setResults(result);
          setResultsSectionOpen(true);
        }
        setIsAnalyzing(false);
        setAnalyzingTaskId(null);
        sessionStorage.removeItem("analyzingTaskId");
      } else if (taskType === 'enhance') {
        if (status === 'SUCCESS') {
          setImprovedPrompt(result.improved_prompt);
          setEnhancedSectionOpen(true);
        }
        setEnhancing(false);
        setEnhancingTaskId(null);
        sessionStorage.removeItem("enhancing");
        sessionStorage.removeItem("enhancingTaskId");
      }
    }
  }, [pollingResult]);


  // Add this function to handle toggling
  const togglePrompt = (id) => {
    setExpandedPrompts(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const uniqueHistory = [];
  const seenPrompts = new Set();

  useEffect(() => {
    const fetchHistory = async () => {
      setHistoryLoading(true);
      try {
        const assetId = sessionStorage.getItem('asset_id');
        const response = await API.get(`${process.env.REACT_APP_DJANGO_API_URL}/prompt-history/?asset_id=${assetId}`);
        const data = response.data;
        setPromptHistory(data);
      } catch (error) {
        console.error('Error fetching history:', error);
      } finally {
        setHistoryLoading(false);
      }
    };
    
    if (activeTab === "history") {
      fetchHistory();
    }
  }, [activeTab]);

  const formatBenchmarkName = (name) => {
    const specialCases = {
      "system_prompt_stealer": "System Prompt Stealer",
      "ethical_compliance": "Ethical Compliance",
      "harmful_behavior": "Harmful Behavior", 
      "custom_benchmark_test": "Custom Benchmark Test",
      "base64_injection": "Base64 Injection",
      "amnesia": "Amnesia Attack",
      "authoritative_role_impersonation": "Authoritative Role Impersonation",
      "affirmative_suffix": "Affirmative Suffix",
      "typoglycemia_attack": "Typoglycemia Attack",
      "do_anything_now_jailbreak": "Do Anything Now (DAN) Jailbreak",
      "aim_jailbreak": "AIM Jailbreak",
      "self_refine": "Self Refine",
      "ucar": "UCAR Jailbreak",
      "complimentary_transition": "Complimentary Transition",
      "contextual_redirection": "Contextual Redirection",
      "linguistic_evasion": "Linguistic Evasion",
    }
    
    if (specialCases[name]) {
      return specialCases[name]
    }
    
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const getScoreColor = (score) => {
    const colors = {
      A: "text-green-400 bg-green-400/10 border-green-400/20",
      B: "text-blue-400 bg-blue-400/10 border-blue-400/20",
      C: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20",
      D: "text-orange-400 bg-orange-400/10 border-orange-400/20",
      E: "text-red-400 bg-red-400/10 border-red-400/20",
      F: "text-red-500 bg-red-500/10 border-red-500/20"
    }
    return colors[score] || "text-gray-400 bg-gray-400/10 border-gray-400/20"
  }

  const getScoreMessage = (score) => {
    const messages = {
      A: "Excellent - No vulnerabilities found",
      B: "Good - Minor vulnerabilities detected",
      C: "Fair - Some vulnerabilities found",
      D: "Poor - Multiple vulnerabilities detected",
      E: "Critical - Many vulnerabilities found",
      F: "Failed - Severe vulnerabilities detected"
    }
    return messages[score] || "Unknown score"
  }

  const handleSubmit = async () => {
    if (isViewer) return;
    if (!prompt.trim()) return
    
    setIsAnalyzing(true);
    setResults(null);
    sessionStorage.removeItem("results");
    setImprovedPrompt("")
    
    try {
      const assetId = sessionStorage.getItem('asset_id');
      const response = await API.post(`${process.env.REACT_APP_DJANGO_API_URL}/system-prompt/`, {
        prompt: prompt,
        asset_id: assetId,
        attack_provider: attackProvider,
        attack_model: attackModel,
      });

      const data = response.data;

      if (data.task_id === "cached") {
        setResults(data.result);
        setResultsSectionOpen(true);
        setIsAnalyzing(false);
        setAnalyzingTaskId(null);
        sessionStorage.removeItem("analyzingTaskId");
      } else {
        setAnalyzingTaskId(data.task_id);
        sessionStorage.setItem("analyzingTaskId", data.task_id);
        startPolling(data.task_id, 'analyze', process.env.REACT_APP_DJANGO_API_URL);
      }
    } catch (error) {
      console.error("Submission failed:", error)
      setResults({
        error: error.message || "Failed to analyze prompt"
      })
      setIsAnalyzing(false);
    }
  }

  const handleEnhance = async () => {
    if (isViewer) return;
    if (!results || results.score === "A") return;

    setEnhancing(true);
    sessionStorage.setItem("enhancing", JSON.stringify(true));
    showNotification("Prompt enhancement has started.", "info");

    try {
      const assetId = sessionStorage.getItem('asset_id');
      const response = await API.post(
        `${process.env.REACT_APP_DJANGO_API_URL}/prompt-enhancer/`,
        {
          prompt: prompt,
          score: results.score,
          failed_tests: results.failed_tests || [],
          passed_tests: results.passed_tests || [],
          asset_id: assetId,
        }
      );

      const data = response.data;
      setEnhancingTaskId(data.task_id);
      sessionStorage.setItem("enhancingTaskId", data.task_id);
      startPolling(data.task_id, 'enhance', process.env.REACT_APP_DJANGO_API_URL);
    } catch (error) {
      setEnhancing(false);
    }
  };

    const handleCancelEnhance = async () => {
    if (!enhancingTaskId) return;

    try {
      await API.post(`${process.env.REACT_APP_DJANGO_API_URL}/cancel-task/${enhancingTaskId}/`);
      stopPolling();
      showNotification("Enhancement cancelled.", "info");
    } catch (error) {
      showNotification("Failed to cancel enhancement.", "error");
    } finally {
      setEnhancing(false);
      setEnhancingTaskId(null);
      sessionStorage.removeItem("enhancing");
      sessionStorage.removeItem("enhancingTaskId");
    }
  };

  promptHistory.forEach(entry => {
    const key = (entry.improved_prompt || entry.original_prompt).trim();
    if (!seenPrompts.has(key)) {
      uniqueHistory.push(entry);
      seenPrompts.add(key);
    }
  });
  
  const canEnhance = results && results.score !== "A" && prompt.trim() && !results.error

  const handleDeleteHistory = async (id) => {
    if (isViewer) return;

    setDeletingId(id);
    try {
      await API.post(`${process.env.REACT_APP_DJANGO_API_URL}/prompt-history/${id}/`);

      setPromptHistory((prev) => prev.filter((entry) => entry.id !== id));
      showNotification("Prompt Evaluation deleted from history");
    } catch (error) {
      console.error('Error deleting history entry:', error);
      showNotification("Failed to delete prompt evaluation", "error");
    } finally {
      setDeletingId(null);
    }
  };

  // Collapsible Section Component
  const CollapsibleSection = ({ title, icon: Icon, isOpen, onToggle, children, className = "", animationClass = "" }) => (
    <div className={`bg-[#1a1a1c] border border-[#232325] rounded-xl overflow-hidden ${className} ${animationClass}`}>
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between text-left hover:bg-[#1f1f21] transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-blue-400" />
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 pt-0 border-t border-[#232325]">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Move loading popup outside the main component container */}
      {loading && <LoadingPopup isVisible={true} />}

      {/* Main Container */}
      <div className="min-h-screen bg-[#0D0D0E] p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">System Prompt Hardening</h1>
            <p className="text-gray-400">Analyze and enhance your system prompts for security vulnerabilities and weaknesses.</p>
          </div>

	          {/* Tab Bar */}
	          <div className="flex mb-8 border-b border-gray-700">
	            {!isViewer && (
	              <button
	                className={`px-6 py-3 -mb-px text-lg font-semibold transition-all duration-300 border-b-2 focus:outline-none ${
	                  activeTab === "prompt"
	                    ? "border-blue-400 text-blue-400"
	                    : "border-transparent text-gray-400 hover:text-blue-300"
	                }`}
	                onClick={() => setActiveTab("prompt")}
	              >
	                <div className="flex items-center gap-2">
	                  <FileText className="h-5 w-5" />
	                  Prompt Hardening
	                </div>
	              </button>
	            )}
	            <button
	              className={`${isViewer ? "" : "ml-4"} px-6 py-3 -mb-px text-lg font-semibold transition-all duration-300 border-b-2 focus:outline-none ${ 
	                activeTab === "history"
	                  ? "border-blue-400 text-blue-400"
	                  : "border-transparent text-gray-400 hover:text-blue-300"
              }`}
              onClick={() => setActiveTab("history")}
            >
              <div className="flex items-center gap-2">
                <History className="h-5 w-5" />
                History
              </div>
            </button>
          </div>
    
      {/* Tab Content */}
	      {!isViewer && activeTab === "prompt" && (
        <div className="space-y-6">
          <h2 className="text-2xl font-semibold text-white mb-6">
            Enter your System Prompt for Hardening
          </h2>
          <div>
            <textarea
              value={prompt}
              onChange={(e) => {
                setPrompt(e.target.value);
                setIsUsingHistoryPrompt(false);
              }}
              rows={10}
              className="w-full p-4 text-base rounded-lg border border-gray-600 bg-gray-800/50 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Enter your system prompt here..."
            />
            <div className="flex gap-4 mt-4 mb-4">
              <div className="w-1/2">
                <EnhancedSelectField
                  name="attackProvider"
                  value={attackProvider}
                  handleChange={handleProviderChange}
                  handleFocus={() => setFocusedField('attackProvider')}
                  handleBlur={() => setFocusedField('')}
                  focusedField={focusedField}
                  options={providerOptions}
                  placeholder="Select Attack Provider"
                  isLoading={providersLoading}
                />
              </div>
              <div className="w-1/2">
                <EnhancedSelectField
                  name="attackModel"
                  value={attackModel}
                  handleChange={(e) => setAttackModel(e.target.value)}
                  handleFocus={() => setFocusedField('attackModel')}
                  handleBlur={() => setFocusedField('')}
                  focusedField={focusedField}
                  options={models}
                  placeholder="Select Attack Model"
                  isLoading={providersLoading}
                />
              </div>
            </div>

                  <div className="flex gap-4">
                    {isUsingHistoryPrompt ? (
                      <>
                        <button
                          onClick={handleEnhance}
                          disabled={!canEnhance || enhancing}
                          className={`px-6 py-3 rounded-xl border font-medium min-w-[120px] flex items-center justify-center transition-all duration-300 ${
                            canEnhance && !enhancing
                              ? "border-green-500/70 bg-green-500/10 text-green-400 hover:bg-green-500/20 hover:border-green-500 hover:shadow-lg hover:shadow-green-500/25 hover:scale-[1.02]"
                              : enhancing
                              ? "border-green-500/70 bg-green-500/10 text-green-400"
                              : "border-gray-600/50 bg-gray-800/20 text-gray-500 cursor-not-allowed"
                          }`}
                        >
                          {enhancing ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-green-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Enhancing...
                            </>
                          ) : (
                            <>
                              <Zap className="h-4 w-4 mr-2" />
                              Enhance
                            </>
                          )}
                        </button>
                      </>
                    ) : (
                      // Show both buttons for new prompts
                      <>
                        <button
                          onClick={handleSubmit}
                          disabled={!prompt.trim() || isAnalyzing || enhancing}
                          className={`px-6 py-3 rounded-xl border font-medium min-w-[120px] flex items-center justify-center transition-all duration-300 ${!isAnalyzing && prompt.trim() && !enhancing
                              ? "border-blue-500/70 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500/25 hover:scale-[1.02]"
                              : isAnalyzing
                                ? "border-blue-500/70 bg-blue-500/10 text-blue-400"
                                : "border-gray-600/50 bg-gray-800/20 text-gray-500 cursor-not-allowed"
                            }`}
                        >
                          {isAnalyzing ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Analyze
                            </>
                          )}
                        </button>
                        <button
                          onClick={handleEnhance}
                          disabled={!canEnhance || enhancing}
                          className={`px-6 py-3 rounded-xl border font-medium min-w-[120px] flex items-center justify-center transition-all duration-300 ${
                            canEnhance && !enhancing
                              ? "border-green-500/70 bg-green-500/10 text-green-400 hover:bg-green-500/20 hover:border-green-500 hover:shadow-lg hover:shadow-green-500/25 hover:scale-[1.02]"
                              : enhancing
                              ? "border-green-500/70 bg-green-500/10 text-green-400"
                              : "border-gray-600/50 bg-gray-800/20 text-gray-500 cursor-not-allowed"
                          }`}
                        >
                          {enhancing ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-green-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Enhancing...
                            </>
                          ) : (
                            <>
                              <Zap className="h-4 w-4 mr-2" />
                              Enhance
                            </>
                          )}
                        </button>
                      </>
                    )}
                    {enhancing && (
                      <button
                        onClick={handleCancelEnhance}
                        className="px-6 py-3 rounded-xl border border-red-500/70 bg-red-500/10 text-red-400 hover:bg-red-500/20 hover:border-red-500 hover:shadow-lg hover:shadow-red-500/25 hover:scale-[1.02] transition-all duration-300 font-medium min-w-[120px] flex items-center justify-center"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </button>
                    )}
                  </div>
            </div>

              {/* Enhanced Prompt Section */}
              {improvedPrompt && (
                <CollapsibleSection
                  title="Enhanced Prompt"
                  icon={Zap}
                  isOpen={enhancedSectionOpen}
                  onToggle={() => setEnhancedSectionOpen(!enhancedSectionOpen)}
                  className=""
                  animationClass="animate-in slide-in-from-top-5 duration-500 ease-out"
                >
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg border border-green-500/30 bg-green-500/5 backdrop-blur-sm">
                      <pre className="text-gray-300 text-sm whitespace-pre-wrap font-mono leading-relaxed overflow-x-auto">
                        {improvedPrompt}
                      </pre>
                    </div>
                    <button
                      onClick={() => {
                        setPrompt(improvedPrompt);
                        showNotification("Enhanced prompt applied successfully");
                      }}
                      className="px-6 py-3 rounded-xl border border-green-500/70 bg-green-500/10 text-green-400 hover:bg-green-500/20 hover:border-green-500 hover:shadow-lg hover:shadow-green-500/25 hover:scale-[1.02] transition-all duration-300 font-medium min-w-[120px] flex items-center justify-center"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Use Enhanced Prompt
                    </button>
                  </div>
                </CollapsibleSection>
              )}

              {/* Results Section */}
              {results && (
                <CollapsibleSection
                  title="Analysis Results"
                  icon={CheckCircle}
                  isOpen={resultsSectionOpen}
                  onToggle={() => setResultsSectionOpen(!resultsSectionOpen)}
                  className=""
                  animationClass="animate-in slide-in-from-bottom-5 duration-500 ease-out"
                >
                  {results.error ? (
                    <div className="p-4 rounded-lg border border-red-500/30 bg-red-500/10 backdrop-blur-sm">
                      <p className="text-red-300">{results.error}</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="flex items-center gap-4">
                        <div className={`px-4 py-2 text-lg rounded-lg border font-bold ${getScoreColor(results.score)}`}>
                          Score: {results.score}
                        </div>
                        <p className="text-lg text-gray-400">{getScoreMessage(results.score)}</p>
                      </div>

                      {results.total_tests_count && (
                        <div className="grid grid-cols-2 gap-4 text-md">
                          <div className="p-4 rounded-lg border border-gray-600/30 bg-gray-800/30 backdrop-blur-sm">
                            <p className="text-gray-400 text-sm">Total Tests</p>
                            <p className="text-white font-semibold text-xl">{results.total_tests_count}</p>
                          </div>
                          <div className="p-4 rounded-lg border border-green-600/30 bg-green-600/10 backdrop-blur-sm">
                            <p className="text-gray-400 text-sm">Resilient Tests</p>
                            <p className="text-green-400 font-semibold text-xl">{results.resilient_tests_count}</p>
                          </div>
                        </div>
                      )}

                      {results.failed_tests && results.failed_tests.length > 0 && (
                        <div className="space-y-3">
                          <h4 className="text-lg font-semibold text-white">Failed Test Cases</h4>
                          <div className="space-y-2">
                            {results.failed_tests.map((test, index) => (
                              <div key={index} className="p-4 rounded-lg border border-red-500/30 bg-red-500/5 backdrop-blur-sm">
                                <div className="flex justify-between items-start mb-2">
                                  <h5 className="font-medium text-red-300">{formatBenchmarkName(test.name || `Test ${index + 1}`)}</h5>
                                  {test.breach_count && (
                                    <span className="text-sm px-2 py-1 rounded bg-red-500/20 text-red-300 border border-red-500/30">
                                      {test.breach_count} breaches
                                    </span>
                                  )}
                                </div>
                                {test.description && (
                                  <p className="text-gray-400 text-sm leading-relaxed">{test.description}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CollapsibleSection>
              )}
        </div>
          )}
      

          {activeTab === "history" && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-white mb-6">
                Prompt History
              </h2>
                {historyLoading ? (
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="animate-pulse">
                        <div className="p-4 rounded-lg border border-gray-600/30 bg-gray-800/30">
                          <div className="h-4 bg-gray-700 rounded w-3/4 mb-2"></div>
                          <div className="h-3 bg-gray-700 rounded w-1/2"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : uniqueHistory.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <History className="mx-auto h-12 w-12 mb-4 opacity-50" />
                    <p className="text-lg">No prompt history available</p>
                    <p className="text-sm">Start analyzing prompts to build your history</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {uniqueHistory.map((entry, index) => (
                      <div 
                        key={entry.id} 
                        className="mb-2 p-4 bg-[#1a1a1c] border border-[#232325] rounded-lg hover:bg-[#1f1f21] transition-colors group relative"
                      >
	                        {/* Delete Button - Top Right Corner */}
	                        {!isViewer && (
	                          <button
	                            onClick={() => handleDeleteHistory(entry.id)}
	                            disabled={deletingId === entry.id}
	                            className="absolute top-3 right-3 p-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 hover:border-red-500/50 transition-all duration-200 opacity-0 group-hover:opacity-100 disabled:opacity-50 disabled:cursor-not-allowed z-10"
	                            title="Delete this entry"
	                          >
	                            {deletingId === entry.id ? (
	                              <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
	                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
	                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
	                              </svg>
	                            ) : (
	                              <X className="h-3 w-3" />
	                            )}
	                          </button>
	                        )}

	                        <div className={`flex items-center justify-between ${isViewer ? "" : "pr-8"}`}>
                          <div className="flex-1 min-w-0 mr-4">
                            {!expandedPrompts[entry.id] && (
                              <div className="text-gray-300 text-sm line-clamp-2">
                                {entry.original_prompt}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className={`px-3 py-1 text-sm rounded-lg border font-bold ${getScoreColor(entry.score)}`}>
                              Score: {entry.score}
                            </span>
                            <button
                              onClick={() => togglePrompt(entry.id)}
                              className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors"
                            >
                              {expandedPrompts[entry.id] ? (
                                <ChevronUp className="h-4 w-4 text-gray-400" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-gray-400" />
                              )}
                            </button>
	                            {!isViewer && (
	                              <button
	                                className="px-4 py-2 rounded-lg border border-blue-500/70 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors"
	                                onClick={() => {
	                                  setPrompt(entry.improved_prompt || entry.original_prompt);
	                                  setResults({
	                                    score: entry.score,
	                                    failed_tests: entry.failed_tests_data,
	                                  });
	                                  setActiveTab("prompt");
	                                  setIsUsingHistoryPrompt(true);
	                                }}
	                              >
	                                Use
	                              </button>
	                            )}
                          </div>
                        </div>

                        {/* Expanded Content */}
                        {expandedPrompts[entry.id] && (
                          <div className="mt-4 pt-4 border-t border-gray-700 space-y-4">
                            <div>
                              <h4 className="text-sm text-gray-400 mb-2 font-medium">Original Prompt:</h4>
                              <pre className="whitespace-pre-wrap bg-black/30 p-3 rounded text-sm text-white overflow-x-auto border border-gray-600/30">
                                {entry.original_prompt}
                              </pre>
                            </div>
                            
                            {entry.improved_prompt && (
                              <div>
                                <h4 className="text-sm text-green-400 mb-2 font-medium">Enhanced Prompt:</h4>
                                <pre className="whitespace-pre-wrap bg-green-500/5 border border-green-500/20 p-3 rounded text-sm text-green-300 overflow-x-auto">
                                  {entry.improved_prompt}
                                </pre>
                              </div>
                            )}

                            {entry.failed_tests_data && entry.failed_tests_data.length > 0 && (
                              <div>
                                <h4 className="text-sm text-red-400 mb-2 font-medium">Failed Test Cases:</h4>
                                <div className="space-y-2">
                                  {entry.failed_tests_data.map((test, testIndex) => (
                                    <div key={testIndex} className="p-3 rounded-lg border border-red-500/30 bg-red-500/5 backdrop-blur-sm">
                                      <h5 className="font-medium text-red-300">{formatBenchmarkName(test.name || `Test ${testIndex + 1}`)}</h5>
                                      {test.description && (
                                        <p className="text-gray-400 text-sm leading-relaxed mt-1">{test.description}</p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
